import React, { useEffect, useState } from "react";
import { Table, Form, Row, Col, Spinner, Dropdown } from "react-bootstrap";
import Card from "src/components/Cards/Basic";
import axios from "src/config/axios";
import { useTranslation } from "react-i18next";
import useFilter from "../../../../global/useFilter";
import moment from "moment";
import { useLocation } from "react-router-dom";
import SimplePagination from "../../../../components/SimplePagination";
import OrderButtonDropdown from "../../../../components/UiComponents/Buttons/OrderButtonDropdown";
import useModal from "src/components/Modal/hooks/useModal";
import PMSDayExportDialog from "src/pages/Dashboard/Accounting/_dialogs/PMSDayExportDialog";
import RevenueExportDialog from "src/pages/Dashboard/Accounting/_dialogs/RevenueExportDialog";
import InvoiceDetailsDialog from "src/pages/Dashboard/Accounting/_dialogs/InvoiceDetailsDialog/InvoiceDetailsDialog";
import AccountingInvoiceRow from "./AccountingInvoiceRow";

const normalizeFiscalReceiptSearch = (value = "") =>
  value.replace(/\D/g, "").slice(0, 8);

const formatFiscalReceiptSearch = (value = "") => {
  const digits = normalizeFiscalReceiptSearch(value);

  if (digits.length <= 4) {
    return digits;
  }

  return `${digits.slice(0, 4)}-${digits.slice(4)}`;
};

const AccountingInvoicesPage = () => {
  const { t } = useTranslation();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const location = useLocation();
  const [pagination, setPagination] = useState([]);
  const {
    isShow: showPmsDayExportDialog,
    toggle: toggleShowPmsDayExportDialog,
  } = useModal();
  const {
    isShow: showRevenueExportDialog,
    toggle: toggleShowRevenueExportDialog,
  } = useModal();

  const { pathname } = location;

  function getDefaultFilter() {
    let values = {
      status: "",
      paid_from: "",
      paid_to: "",
      from: moment().startOf("year"),
      to: moment().endOf("month"),
      fiscal_printed: "",
      fiscal_receipt_search: "",
      page: 1,
      order: "created_at_desc",
    };

    return values;
  }

  const { filter, setFilter } = useFilter(pathname, getDefaultFilter());
  const [fiscalReceiptSearchInput, setFiscalReceiptSearchInput] = useState(
    formatFiscalReceiptSearch(filter.fiscal_receipt_search)
  );

  function getInvoices() {
    const params = Object.fromEntries(
      Object.entries(filter).filter(([_, v]) => v != "null" && v !== "")
    );

    setLoading(true);

    axios
      .get("api/v1/accounting/invoices.json", { params: params })
      .then(({ data }) => {
        setInvoices(data.entries);
        setPagination(data.pagination);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }

  const goToPage = (page) => {
    setFilter((prev) => {
      return { ...prev, page: page };
    });
  };

  function handleDateFilterChange(field, e) {
    const value = e.target.value;
    setFilter((prev) => ({ ...prev, [field]: value ? moment(value) : null }));
  }

  function handleSelectFilterChange(field, e) {
    const value = e.target.value;
    setFilter((prev) => ({ ...prev, [field]: value }));
  }

  function handleFiscalReceiptSearchChange(e) {
    const formattedValue = formatFiscalReceiptSearch(e.target.value);
    const digits = normalizeFiscalReceiptSearch(formattedValue);

    setFiscalReceiptSearchInput(formattedValue);
    setFilter((prev) => ({
      ...prev,
      fiscal_receipt_search: digits.length === 8 ? digits : "",
    }));
  }

  useEffect(() => {
    if (filter.page === 1) {
      getInvoices();
    } else {
      goToPage(1);
    }
  }, [
    filter.from,
    filter.to,
    filter.status,
    filter.paid_from,
    filter.paid_to,
    filter.fiscal_printed,
    filter.fiscal_receipt_search,
    filter.order,
  ]);

  useEffect(() => {
    getInvoices();
  }, [filter.page]);

  useEffect(() => {
    setFiscalReceiptSearchInput(
      formatFiscalReceiptSearch(filter.fiscal_receipt_search)
    );
  }, [filter.fiscal_receipt_search]);

  return (
    <>
      <Card title={t("accounting.page.title")}>
        <Form className="mb-3">
          <div className={"d-flex"}>
            <OrderButtonDropdown
              selected={filter.order}
              className={"mb-2 mr-2"}
              orders={[
                { key: "created_at_asc", value: "Erstellt am aufsteigend" },
                { key: "created_at_desc", value: "Erstellt am absteigend" },
                { key: "paid_at_asc", value: "Bezahlt am aufsteigend" },
                { key: "paid_at_desc", value: "Bezahlt am absteigend" },
              ]}
              callback={(selected_value) => {
                setFilter({
                  ...filter,
                  order: selected_value,
                });
              }}
            />
            <Dropdown>
              <Dropdown.Toggle size={"sm"} id="dropdown-basic">
                Export
              </Dropdown.Toggle>

              <Dropdown.Menu>
                <Dropdown.Item onClick={toggleShowPmsDayExportDialog}>
                  {t("accounting.pms_day_export_dialog.title")}
                </Dropdown.Item>
                <Dropdown.Item onClick={toggleShowRevenueExportDialog}>
                  {t("accounting.revenue_export_dialog.title")}
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </div>
          <Row className="align-items-center mb-2">
            <Col md={3}>
              <Form.Label>{t("accounting.page.filters.from")}</Form.Label>
              <Form.Control
                type="date"
                value={
                  filter.from ? filter.from.format(moment.HTML5_FMT.DATE) : ""
                }
                onChange={(e) => handleDateFilterChange("from", e)}
              />
            </Col>
            <Col md={3}>
              <Form.Label>{t("accounting.page.filters.to")}</Form.Label>
              <Form.Control
                type="date"
                value={filter.to ? filter.to.format(moment.HTML5_FMT.DATE) : ""}
                onChange={(e) => handleDateFilterChange("to", e)}
              />
            </Col>
            <Col md={3}>
              <Form.Label>
                {t("accounting.page.filters.status.label")}
              </Form.Label>
              <Form.Control
                as={"select"}
                value={filter.status}
                onChange={(e) => handleSelectFilterChange("status", e)}
              >
                <option value="">
                  {t("accounting.page.filters.status.all")}
                </option>
                <option value="paid">
                  {t("accounting.page.filters.status.paid")}
                </option>
                <option value="unpaid">
                  {t("accounting.page.filters.status.unpaid")}
                </option>
              </Form.Control>
            </Col>
            <Col md={3}></Col>
          </Row>
          <Row className="align-items-end">
            <Col md={3}>
              <Form.Label>{t("accounting.page.filters.paid_from")}</Form.Label>
              <Form.Control
                type="date"
                value={
                  filter.paid_from
                    ? filter.paid_from.format(moment.HTML5_FMT.DATE)
                    : ""
                }
                onChange={(e) => handleDateFilterChange("paid_from", e)}
              />
            </Col>
            <Col md={3}>
              <Form.Label>{t("accounting.page.filters.paid_to")}</Form.Label>
              <Form.Control
                type="date"
                value={
                  filter.paid_to
                    ? filter.paid_to.format(moment.HTML5_FMT.DATE)
                    : ""
                }
                onChange={(e) => handleDateFilterChange("paid_to", e)}
              />
            </Col>
            <Col md={3}>
              <Form.Label>
                {t("accounting.page.filters.fiscal_printed.label")}
              </Form.Label>

              <Form.Control
                as={"select"}
                value={filter.fiscal_printed}
                onChange={(e) => handleSelectFilterChange("fiscal_printed", e)}
              >
                <option value="">
                  {t("accounting.page.filters.fiscal_printed.all")}
                </option>
                <option value="false">
                  {t("accounting.page.filters.fiscal_printed.no")}
                </option>
                <option value="true">
                  {t("accounting.page.filters.fiscal_printed.yes")}
                </option>
              </Form.Control>
            </Col>
            <Col md={3}>
              <Form.Label>
                {t("accounting.page.filters.fiscal_receipt_search.label")}
              </Form.Label>
              <Form.Control
                type="text"
                inputMode="numeric"
                placeholder={t(
                  "accounting.page.filters.fiscal_receipt_search.placeholder"
                )}
                value={fiscalReceiptSearchInput}
                onChange={handleFiscalReceiptSearchChange}
              />
            </Col>
          </Row>
        </Form>

        {loading ? (
          <div className="text-center p-4">
            <Spinner animation="border" variant="primary" />
          </div>
        ) : (
          <>
            <div className="ui-table-container mb-3">
              <Table
                className="table align-middle mb-0 bg-white"
                responsive
                hover
              >
                <thead>
                  <tr>
                    <th>{t("accounting.page.table.invoice_number")}</th>
                    <th>{t("accounting.page.table.total")}</th>
                    <th>{t("accounting.page.table.paid")}</th>
                    <th>{t("accounting.page.table.open")}</th>
                    <th>{t("accounting.page.table.status")}</th>
                    <th>{t("accounting.page.table.paid_at")}</th>
                    <th>{t("accounting.page.table.receipt")}</th>
                    <th>{t("accounting.page.table.created_at")}</th>
                    <th>{t("accounting.page.table.options")}</th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.length === 0 ? (
                    <tr>
                      <td colSpan={9} className="text-center text-muted">
                        {t("accounting.page.table.no_results")}
                      </td>
                    </tr>
                  ) : (
                    invoices.map((invoice) => (
                      <AccountingInvoiceRow
                        key={invoice.id}
                        invoice={invoice}
                        onDetailsClick={setSelectedInvoice}
                      />
                    ))
                  )}
                </tbody>
              </Table>
            </div>
            <SimplePagination
              pagination={pagination}
              onClick={(page) => setFilter((prev) => ({ ...prev, page: page }))}
            />
          </>
        )}
      </Card>

      {selectedInvoice && (
        <InvoiceDetailsDialog
          invoice={selectedInvoice}
          handleClose={() => setSelectedInvoice(null)}
        />
      )}

      {showPmsDayExportDialog && (
        <PMSDayExportDialog
          handleSubmit={toggleShowPmsDayExportDialog}
          handleClose={toggleShowPmsDayExportDialog}
        />
      )}

      {showRevenueExportDialog && (
        <RevenueExportDialog
          handleSubmit={toggleShowRevenueExportDialog}
          handleClose={toggleShowRevenueExportDialog}
        />
      )}
    </>
  );
};

export default AccountingInvoicesPage;
